import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, connect } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HandymanIcon from "@mui/icons-material/Handyman";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { getOrderDetails } from "../Orders/actions";
import { getOrderProposals, updateOrderProposal } from "../Orders/actions";
import { Link } from "react-router-dom";
import { OrderProposalStatus } from "../../configuration/api/apiSchema";

interface Props {
  getOrderDetails: (orderId: string) => void;
  getOrderProposals: (orderId: string) => void;
  updateOrderProposal: (orderProposalId: string, status: "Confirmed" | "Rejected") => void;
}

const OrderProposalsPage: React.FC<Props> = (props) => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const currentUserId = useSelector((state: HandymanAppState) => state.user.currentUserDetails?.id);
  const { orderDetails } = useSelector((state: HandymanAppState) => state.order);
  const orderProposals = useSelector((state: HandymanAppState) => state.order.orderProposals || []);
  const orderProposalsLoading = useSelector((state: HandymanAppState) => state.order.orderProposalsLoading);
  const orderProposalsError = useSelector((state: HandymanAppState) => state.order.orderProposalsError);

  useEffect(() => {
    if (orderId) {
      props.getOrderDetails(orderId);
      props.getOrderProposals(orderId);
    }
  }, [props, orderId]);

  const handleApproveProposal = async (proposalId: string) => {
    await props.updateOrderProposal(proposalId, "Confirmed");
    if (orderId) {
      props.getOrderProposals(orderId);
    }
  };

  const handleRejectProposal = async (proposalId: string) => {
    await props.updateOrderProposal(proposalId, "Rejected");
    if (orderId) {
      props.getOrderProposals(orderId);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Order
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Order Proposals
          </Typography>
        </Box>
        {orderDetails && (
          <Typography variant="subtitle1" color="text.secondary">
            Order: {orderDetails.name || "Order #" + orderId?.substring(0, 8)}
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {orderProposalsLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : orderProposalsError ? (
          <Box p={2}>
            <Typography color="error">
              Error loading proposals: {orderProposalsError}
            </Typography>
          </Box>
        ) : orderProposals.length === 0 ? (
          <Typography color="text.secondary" p={2}>
            No proposals for this order yet
          </Typography>
        ) : (
          <Box>
            {orderProposals.map((proposal) => {
              const isSentByCurrentUser =
                (proposal.isInitiatedByCustomer && currentUserId === proposal.customerId) ||
                (!proposal.isInitiatedByCustomer && currentUserId === proposal.handymanId);

              const canAct = proposal.isInitiatedByCustomer
                ? currentUserId === proposal.handymanId
                : currentUserId === proposal.customerId;

              return (
                <Box
                  key={proposal.id}
                  p={3}
                  mb={2}
                  sx={{
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <HandymanIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">
                        <Link 
                          to={`/browse-handyman/${proposal?.handyman?.id}`} 
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {proposal?.handyman
                            ? `${proposal.handyman.firstName} ${proposal.handyman.lastName}`
                            : "N/A"}
                        </Link>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={isSentByCurrentUser ? "Sent" : "Received"}
                        color={isSentByCurrentUser ? "primary" : "secondary"}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={proposal.status}
                        color={
                          proposal.status === OrderProposalStatus.Confirmed
                            ? "success"
                            : proposal.status === OrderProposalStatus.Rejected
                            ? "error"
                            : "default"
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <Typography variant="body1" color="text.primary">
                      {proposal.order?.description || "No description provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Price: ${proposal.order?.price || 0}
                    </Typography>
                  </Box>
                  {canAct && proposal.status === OrderProposalStatus.Requested && (
                    <Box mt={2} display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleApproveProposal(proposal.id!)}
                        size="small"
                      >
                        Accept Proposal
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRejectProposal(proposal.id!)}
                        size="small"
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

const mapDispatchToProps = {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
};

export default connect(null, mapDispatchToProps)(OrderProposalsPage); 